import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStores } from "stores";
import { Alert, Col, Collapse, CollapseProps, Flex, Radio, Spin, Tooltip, Typography } from "antd";

import { ItemInfoBlock } from "components/ItemInfo";
import { routes } from "constants/routes";
import { getStatusMessage, setDefaultBankDetails } from "services";
import { parseErrorMessage } from "utils";
import { getColumns } from "./getColumns";
import { DeleteBankDetails } from "./DeleteBankDetails";
import { useNotificationContext } from "providers";
import { BankAccountGrid, NOTIFICATION_TYPE_ENUM } from "interfaces";

import styles from "./bank.module.css";

export const BankDetails = observer(() => {
  const { user, bankAccountsStore, bankAccountsDetailStore, companyCard } = useStores();
  const { openNotification } = useNotificationContext();
  const { bankAccounts } = bankAccountsStore;
  const [sortedBankAccounts, setSortedBankAccounts] = useState<BankAccountGrid[]>([]);

  const [loading, setLoading] = useState(false);
  const [defaultBankAccount, setDefaultBankAccount] = useState<string | undefined>(undefined);
  const [openedBankAccountsId, setOpenedBankAccountsId] = useState<string[]>([]);

  const { isSeniorCustomer, isSeniorLP, isSystemAdmin } = user.roles;

  const isProfilePage = window.location.pathname.replace("/", "") === routes.profile;

  const showIsRadio = isProfilePage && (isSeniorCustomer || isSeniorLP || isSystemAdmin);

  const canArchiveBankAccount = (isSeniorCustomer || isSeniorLP || isSystemAdmin) && isProfilePage;

  const companyId = isProfilePage ? user.userInfo?.company_id : companyCard.card.id;

  useEffect(() => {
    if (companyId) {
      bankAccountsStore.fetchBankAccounts(companyId);
    }
  }, [companyId, bankAccountsStore]);

  useEffect(() => {
    if (bankAccounts.length > 0) {
      const defaultAccount = bankAccounts.find((account) => account.isDefault);

      if (defaultAccount) {
        setDefaultBankAccount(defaultAccount.id);
      }
    }

    setSortedBankAccounts(bankAccounts.sort((a, b) => (a.isDefault ? -1 : 1)));
  }, [bankAccounts]);

  const handleCollapseChange = async (keys: string[]) => {
    const newOpenedAccount = keys.find((key) => !openedBankAccountsId.includes(key));

    if (companyId && newOpenedAccount) {
      await bankAccountsDetailStore.fetchBankAccountDetails(companyId, newOpenedAccount);
    }

    setOpenedBankAccountsId(keys);
  };

  const changeDefaultBankAccount = async (index: string) => {
    if (!companyId) return;
    setLoading(true);

    await setDefaultBankDetails({ id: companyId, accountId: index })
      .then(() => {
        openNotification({
          message: "Реквизиты по умолчанию установлены",
          type: NOTIFICATION_TYPE_ENUM.SUCCESS,
        });
        setDefaultBankAccount(index);

        setTimeout(() => {
          bankAccountsStore.fetchBankAccounts(companyId);
        }, 1000);
      })
      .catch((e) => {
        openNotification({
          description: parseErrorMessage(e) ?? "Не удалось установить реквизиты по умолчанию",
          type: NOTIFICATION_TYPE_ENUM.ERROR,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const collapseItems: CollapseProps["items"] = sortedBankAccounts.map((item) => {
    return {
      key: item.id,
      label: (
        <Flex align="start" gap={"middle"}>
          {showIsRadio && (
            <Tooltip title={`${!item.isDefault ? "Использовать по умолчанию" : ""}`} className={styles.radioBtn}>
              <Radio
                onClick={(e) => {
                  e.stopPropagation();
                }}
                onChange={() => {
                  changeDefaultBankAccount(item.id);
                }}
                name="bankDetails"
                value={item.id}
                checked={defaultBankAccount === item.id}
                disabled={loading}
              />
            </Tooltip>
          )}
          <Flex align="center" justify="space-between" className={`flex-grow ${styles.label}`}>
            <Col>
              <Flex gap="small" align="center">
                <Typography.Title level={5} className={`${styles.labelTitle} margin-none`}>
                  {item.accountName}
                </Typography.Title>
                {bankAccounts.length > 1 && canArchiveBankAccount && <DeleteBankDetails id={item.id} />}
              </Flex>
              <Typography.Text className={`text-description ${styles.labelDescription}`}>
                <Flex gap="large">
                  <span>{item.bankName}</span>
                  <span>{item.accountNumber}</span>
                </Flex>
              </Typography.Text>
            </Col>
            {item.isDefault && (
              <Typography.Text className={`text-description ${styles.labelRight}`}>Выбран по умолчанию</Typography.Text>
            )}
          </Flex>
        </Flex>
      ),
      children: (
        <Spin spinning={bankAccountsDetailStore.isPending && !bankAccountsDetailStore.getBankAccountDetails(item.id)}>
          {((bankAccountsDetailStore.fetchStatus === "success" &&
            bankAccountsDetailStore.getBankAccountDetails(item.id)) ||
            bankAccountsDetailStore.getBankAccountDetails(item.id)) && (
            <ItemInfoBlock items={getColumns(bankAccountsDetailStore.getBankAccountDetails(item.id)!)} key={item.id} />
          )}
          {bankAccountsDetailStore.fetchStatus === "error" && bankAccountsDetailStore.fetchError && (
            <Alert
              message={getStatusMessage(bankAccountsDetailStore.fetchError.status)}
              description={parseErrorMessage(bankAccountsDetailStore.fetchError)}
              type="error"
              showIcon
              className="margin-top-lg"
            />
          )}
        </Spin>
      ),
    };
  });

  return (
    <Spin spinning={bankAccountsStore.isPending}>
      {bankAccountsStore.fetchStatus === "success" && (
        <Collapse
          items={collapseItems}
          expandIconPosition="end"
          bordered={false}
          onChange={(e) => handleCollapseChange(e)}
          className={`default-collapse ${showIsRadio ? "collapse-content-padding" : ""}`}
        />
      )}
      {bankAccountsStore.fetchStatus === "error" && bankAccountsStore.fetchError && (
        <Alert
          message={getStatusMessage(bankAccountsStore.fetchError.status)}
          description={parseErrorMessage(bankAccountsStore.fetchError)}
          type="error"
          showIcon
          className="margin-top-lg"
        />
      )}
    </Spin>
  );
});
